import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Sparkles, FileText, HelpCircle, Zap, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useMessageTemplates } from "@/hooks/useMessageTemplates";
import solveLogoNew from "@/assets/solve-iagro-logo-new.png";
import { parseSimpleMarkdown } from "@/lib/markdown-utils";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface SolveAssistantChatProps {
  currentPage?: string;
  onClose: () => void;
}

// Helper to detect template JSON in AI response
const detectTemplateInResponse = (content: string) => {
  const regex = /\[TEMPLATE_JSON\]([\s\S]*?)\[\/TEMPLATE_JSON\]/;
  const match = content.match(regex);
  if (match) {
    try {
      return JSON.parse(match[1].trim());
    } catch {
      return null;
    }
  }
  return null;
};

// Helper to get context label
const getContextLabel = (tipo_disparo: string | null): string => {
  const labels: Record<string, string> = {
    boletos: "Boletos",
    documentos_juridicos: "Documentos Jurídicos",
    alertas_climaticos: "Alertas Climáticos",
  };
  return tipo_disparo ? labels[tipo_disparo] || "Genérico" : "Genérico";
};

// Contextual suggestions based on current page
const getContextualSuggestions = (page: string) => {
  const suggestions: Record<string, string[]> = {
    "/disparos": [
      "Criar template de cobrança amigável",
      "Criar template de lembrete urgente",
      "Quais variáveis posso usar em boletos?",
    ],
    "/follow-up-boletos": [
      "Criar sequência de follow-up em 3 etapas",
      "Criar template de última tentativa",
      "Melhores práticas de cobrança recorrente",
    ],
    "/templates": [
      "Criar template profissional de cobrança",
      "Criar template de boas-vindas",
      "Como usar variáveis dinâmicas?",
    ],
    "/garantias": [
      "Como analisar documentos CPR?",
      "Exportar relatório em Excel",
      "O que são CPRs físicas e financeiras?",
    ],
    "/chatbot-config": [
      "Configurar horário de atendimento",
      "Como usar palavras-chave?",
      "Personalizar respostas do bot",
    ],
  };

  return suggestions[page] || [
    "Como funciona a plataforma?",
    "Dicas de automação",
    "Criar template personalizado",
  ];
};

export function SolveAssistantChat({ currentPage = "/", onClose }: SolveAssistantChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Olá! 🌾 Sou o Solve, seu assistente virtual da Solve.AI. Como posso ajudar você hoje?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { createTemplate } = useMessageTemplates();

  const suggestions = getContextualSuggestions(currentPage);

  const handleSaveTemplate = async (templateData: any) => {
    try {
      await createTemplate({
        title: templateData.title,
        tag: templateData.tag,
        body: templateData.body,
        tipo_disparo: templateData.tipo_disparo,
        categoria: templateData.tag,
      });
      toast.success("Template salvo na sua biblioteca! 🌾");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Erro ao salvar template");
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    
    // Add user message
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("solve-assistant-chat", {
        body: {
          messages: [...messages, { role: "user", content: userMessage }],
          currentPage,
        },
      });

      if (error) throw error;

      if (data?.message) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message },
        ]);
      }
    } catch (error) {
      console.error("Error calling Solve assistant:", error);
      toast.error("Ops! Tive um problema ao processar sua mensagem. Tente novamente.");
      
      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Desculpe, tive um problema ao processar sua mensagem. Por favor, tente novamente! 🌱",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="w-[420px] h-[600px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <motion.img
              src={solveLogoNew}
              alt="Solve"
              className="h-10 w-10 rounded-full bg-white p-1"
              animate={{
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 3,
                ease: "easeInOut",
              }}
            />
            <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              Solve <Sparkles className="h-4 w-4" />
            </h3>
            <p className="text-xs opacity-90">Assistente Solve.AI • Online</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-primary-foreground hover:bg-primary-foreground/20"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Messages Area */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
        <div className="space-y-4">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => {
              const detectedTemplate = message.role === "assistant" ? detectTemplateInResponse(message.content) : null;
              const contentWithoutJson = detectedTemplate 
                ? message.content.replace(/\[TEMPLATE_JSON\][\s\S]*?\[\/TEMPLATE_JSON\]/, "").trim()
                : message.content;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                      {message.role === "assistant" ? parseSimpleMarkdown(contentWithoutJson) : contentWithoutJson}
                    </div>
                  </div>

                  {detectedTemplate && (
                    <Card className="mt-3 max-w-[85%] border-primary/30 bg-gradient-to-r from-primary/5 to-green-500/5">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm">{detectedTemplate.title}</span>
                          </div>
                          <Badge variant="secondary" className="text-xs">{detectedTemplate.tag}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-3">
                        <p className="text-xs text-muted-foreground line-clamp-3">
                          {detectedTemplate.body}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {getContextLabel(detectedTemplate.tipo_disparo)}
                          </Badge>
                          <Button 
                            size="sm" 
                            onClick={() => handleSaveTemplate(detectedTemplate)}
                            className="h-7 text-xs"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Salvar na Biblioteca
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Loading indicator */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-muted text-foreground rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Solve está digitando...</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Suggestions */}
      {!isLoading && messages.length <= 2 && (
        <div className="px-4 pb-3">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Sugestões rápidas:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => handleSuggestionClick(suggestion)}
                className="text-xs h-7 rounded-full"
              >
                {index === 0 && <FileText className="h-3 w-3 mr-1" />}
                {index === 1 && <HelpCircle className="h-3 w-3 mr-1" />}
                {index === 2 && <Zap className="h-3 w-3 mr-1" />}
                {suggestion}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-border p-4">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua mensagem..."
            className="min-h-[44px] max-h-[120px] resize-none"
            disabled={isLoading}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-11 w-11 shrink-0"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Pressione Enter para enviar • Shift+Enter para nova linha
        </p>
      </div>
    </div>
  );
}
